import { useEffect, useState } from 'react';
import { Youtube, Radio } from 'lucide-react';
import { api } from '../lib/api';
import { SectionLabel, Reveal } from '../components/Section';

function StreamCard({ s, index }) {
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div className="card-surface card-surface-hover overflow-hidden h-full flex flex-col" data-testid={`stream-card-${s.id}`}>
        {s.thumbnail_url && (
          <div className="relative aspect-video overflow-hidden">
            <img src={s.thumbnail_url} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2e] to-transparent" />
            {s.is_live && (
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#e8173a] text-white text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Live
              </span>
            )}
          </div>
        )}
        <div className="p-5 flex flex-col gap-3 flex-1">
          <div className="flex-1">
            <h3 className="text-white font-bold text-sm leading-snug">{s.title}</h3>
            <p className="mt-1.5 text-xs text-[#8a8a8a]">
              {[s.streamer, s.game].filter(Boolean).join(' • ')}
            </p>
          </div>
          {s.youtube_url && (
            <a
              data-testid={`stream-watch-${s.id}`}
              href={s.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#e8173a] hover:bg-[#c0102e] text-white text-xs font-extrabold uppercase tracking-wider px-4 py-3 rounded-lg transition-all duration-300 active:scale-95"
            >
              <Youtube className="w-4 h-4" />
              Watch on YouTube
            </a>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Streams() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.streams().then(setData).catch(() => setData({ live: [], past: [] }));
  }, []);

  const live = data?.live || [];
  const past = data?.past || [];

  return (
    <main className="min-h-screen" data-testid="streams-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="streams-label">Live Streams</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl">
            Watch the arena <span className="text-[#e8173a]">unfold</span>
          </h1>
        </Reveal>

        {data === null ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-64 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid={`stream-skeleton-${i}`} />
            ))}
          </div>
        ) : (
          <>
            <div className="mt-14">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#e8173a] flex items-center gap-2" data-testid="streams-live-heading">
                <Radio className="w-4 h-4" /> Live Now
              </h2>
              {live.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {live.map((s, i) => (
                    <StreamCard key={s.id} s={s} index={i} />
                  ))}
                </div>
              ) : (
                <p className="mt-6 text-sm text-[#8a8a8a]" data-testid="streams-live-empty">
                  No live streams right now — we go live whenever a featured match starts.
                </p>
              )}
            </div>
            {past.length > 0 && (
              <div className="mt-16">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a]" data-testid="streams-past-heading">
                  Past Streams
                </h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {past.map((s, i) => (
                    <StreamCard key={s.id} s={s} index={i} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
